import React, {
    useState
} from 'react';

import axios from 'axios';

import {
    useNavigate
} from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { ChevronRight, ShieldCheck, ArrowLeft, KeyRound, Mail, Lock } from 'lucide-react';

const Login = () => {
    const { isDarkMode } = useTheme();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [show2FA, setShow2FA] = useState(false);
    const [tempToken, setTempToken] = useState('');
    const [otpCode, setOtpCode] = useState('');
    
    // Forgot Password States
    const [forgotMode, setForgotMode] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotSuccess, setForgotSuccess] = useState('');
    
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const response = await axios.post('http://localhost:4000/api/auth/login', {
                email, password
            });
            
            if (response.data.requires2FA) {
                setTempToken(response.data.tempToken);
                setShow2FA(true);
                setLoading(false);
                return;
            }

            await processLoginSuccess(response.data);
        } catch (err) {
            if (err.response && err.response.data) {
                setError(err.response.data.error || 'Credenciales inválidas');
            } else {
                setError('Error al conectar con el servidor');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerify2FA = async (e) => {
        e.preventDefault();
        if (!otpCode || otpCode.length !== 6) {
            setError('Ingresa un código de 6 dígitos válido.');
            return;
        }
        setError('');
        setLoading(true);
        try {
            const response = await axios.post('http://localhost:4000/api/auth/verify-2fa', {
                tempToken,
                code: otpCode
            });
            await processLoginSuccess(response.data);
        } catch (err) {
            if (err.response && err.response.data) {
                setError(err.response.data.error || 'Código incorrecto o expirado');
            } else {
                setError('Error al conectar con el servidor');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setError('');
        setForgotSuccess('');
        setLoading(true);
        try {
            const response = await axios.post('http://localhost:4000/api/auth/forgot-password', {
                email: forgotEmail
            });
            setForgotSuccess(response.data.message);
        } catch (err) {
            if (err.response && err.response.data) {
                setError(err.response.data.error || 'Error al procesar la solicitud.');
            } else {
                setError('Error al conectar con el servidor');
            }
        } finally {
            setLoading(false);
        }
    };

    const processLoginSuccess = async (data) => {
        const { token, role, company_id, name, mustChangePassword } = data;
        
        // Guardamos la sesión
        localStorage.setItem('token', token);
        localStorage.setItem('userRole', role);
        if (company_id) localStorage.setItem('companyId', company_id);
        if (name) localStorage.setItem('userName', name);
        if (mustChangePassword) localStorage.setItem('mustChangePassword', 'true');

        // Obtener y setear proyecto por defecto para roles operativos sin selector de proyecto
        if (role === 'PROJECT_MANAGER' || role === 'DEMO_USER' || role === 'VIEWER') {
            try {
                const projectsRes = await axios.get('http://localhost:4000/api/projects', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (projectsRes.data && projectsRes.data.length > 0) {
                    localStorage.setItem('activeProjectId', projectsRes.data[0].id);
                    localStorage.setItem('activeProjectName', projectsRes.data[0].name);
                }
            } catch (projectsErr) {
                console.error("Error al obtener proyecto por defecto:", projectsErr);
            }
        }
        
        // Redirección condicional basada en el rol del usuario 
        if (role === 'SUPER_ADMIN') {
            navigate('/admin-global');
        } else if (role === 'COMPANY_ADMIN') {
            navigate('/projects');
        } else if (role === 'PROJECT_MANAGER' || role === 'DEMO_USER') {
            navigate('/dashboard');
        } else if (role === 'VIEWER') {
            navigate('/cliente');
        } else {
            navigate('/projects');
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-[#03091e] font-sans relative overflow-hidden">
            {/* Ambient Lighting Background Blobs */}
            <div className="absolute top-[-20%] left-[-20%] w-[70%] h-[70%] rounded-full bg-blue-600/10 blur-[130px] pointer-events-none"></div>
            <div className="absolute bottom-[-20%] right-[-20%] w-[70%] h-[70%] rounded-full bg-purple-600/10 blur-[130px] pointer-events-none"></div>
            
            <div className="bg-slate-900/40 backdrop-blur-2xl p-8 rounded-[32px] border border-white/5 shadow-[0_20px_50px_rgba(0,0,0,0.4)] w-full max-w-md relative z-10 hover:border-blue-500/20 transition-all duration-500">
                <div className="text-center mb-8 flex flex-col items-center">
                    <img src="/logo-blanco-mark.png" alt="Archi24/7 Logo" className="h-24 w-auto object-contain mb-3 drop-shadow-[0_0_15px_rgba(59,130,246,0.3)] animate-pulse" />
                    <div className="font-sans text-center select-none leading-none tracking-tight">
                        <span className="text-2xl font-black uppercase tracking-wider text-white">ARCHI</span>
                        <span className="text-2xl font-light ml-2 text-blue-400">24/7</span>
                    </div>
                    <p className="text-slate-400 font-bold text-xs uppercase tracking-widest mt-3">Plataforma Operativa</p>
                </div>

                {error && (
                    <div className="bg-rose-500/10 border border-rose-500/35 text-rose-400 text-xs font-bold px-4 py-3 rounded-2xl mb-6 text-center">
                        {error}
                    </div>
                )}

                {forgotSuccess && (
                    <div className="bg-emerald-500/10 border border-emerald-500/35 text-emerald-400 text-xs font-bold px-4 py-3 rounded-2xl mb-6 text-center">
                        {forgotSuccess}
                    </div>
                )}

                {/* FLOW 1: Forgot Password Form */}
                {forgotMode ? (
                    <form onSubmit={handleForgotPassword} className="space-y-5">
                        <div className="text-center mb-4">
                            <KeyRound className="mx-auto text-blue-400 mb-2" size={36} />
                            <h3 className="text-sm font-bold text-white">¿Olvidaste tu contraseña?</h3>
                            <p className="text-xs text-slate-400 mt-1">Ingresa tu correo registrado y te enviaremos una clave de acceso temporal de 24 horas.</p>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Correo Electrónico</label>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                                    <Mail size={16} />
                                </span>
                                <input
                                    type="email"
                                    value={forgotEmail}
                                    onChange={(e) => setForgotEmail(e.target.value)}
                                    required
                                    className="w-full bg-slate-950/60 border border-white/5 text-white pl-10 pr-4 py-3 rounded-2xl focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all font-sans text-sm"
                                    placeholder="ejemplo@holatelecom.com"
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold py-3 mt-4 rounded-2xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-blue-500/10"
                        >
                            {loading ? <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span> : 'Enviar Instrucciones'}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setForgotMode(false); setForgotSuccess(''); setError(''); }}
                            className="w-full border border-white/5 text-slate-400 hover:text-white font-bold py-3 rounded-2xl transition-all flex items-center justify-center gap-1.5 cursor-pointer text-xs bg-white/5"
                        >
                            <ArrowLeft size={14} /> Volver al Inicio
                        </button>
                    </form>
                ) : !show2FA ? (
                    /* FLOW 2: Standard Login Form */
                    <form onSubmit={handleLogin} className="space-y-5">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Correo Corporativo</label>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                                    <Mail size={16} />
                                </span>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full bg-slate-950/60 border border-white/5 text-white pl-10 pr-4 py-3 rounded-2xl focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all font-sans text-sm"
                                    placeholder="admin@archi247.com"
                                />
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Contraseña</label>
                                <button
                                    type="button"
                                    onClick={() => { setForgotMode(true); setError(''); }}
                                    className="text-[10px] text-blue-400 hover:text-blue-300 font-bold tracking-wide transition-colors cursor-pointer"
                                >
                                    ¿Olvidaste tu contraseña?
                                </button>
                            </div>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                                    <Lock size={16} />
                                </span>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                    className="w-full bg-slate-950/60 border border-white/5 text-white pl-10 pr-4 py-3 rounded-2xl focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all font-sans text-sm"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold py-3 mt-4 rounded-2xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-blue-500/10"
                        >
                            {loading ? <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span> : 'Ingresar al Portal'}
                        </button>
                    </form>
                ) : (
                    /* FLOW 3: 2FA Authentication Form */
                    <form onSubmit={handleVerify2FA} className="space-y-5">
                        <div className="text-center mb-4">
                            <ShieldCheck className="mx-auto text-blue-400 mb-2" size={40} />
                            <h3 className="text-sm font-bold text-white">Doble Factor de Autenticación</h3>
                            <p className="text-xs text-slate-400 mt-1">Hemos enviado un código OTP de 6 dígitos a tu correo registrado <strong>{email}</strong>.</p>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 text-center">Código de Seguridad</label>
                            <input
                                type="text"
                                maxLength={6}
                                value={otpCode}
                                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                                required
                                className="w-full bg-slate-950/60 border border-white/5 text-white text-center text-2xl tracking-[10px] font-bold px-4 py-3 rounded-2xl focus:outline-none focus:border-blue-500 transition-all"
                                placeholder="000000"
                            />
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => { setShow2FA(false); setOtpCode(''); setError(''); }}
                                className="w-1/3 border border-white/5 text-slate-400 font-bold py-3 rounded-2xl hover:bg-white/5 transition-all flex items-center justify-center gap-1 cursor-pointer text-xs"
                            >
                                <ArrowLeft size={14} /> Atrás
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-2/3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold py-3 rounded-2xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-blue-500/10"
                            >
                                {loading ? <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span> : 'Verificar'}
                            </button>
                        </div>
                    </form>
                )}

                <div className="mt-6 pt-4 border-t border-white/5">
                    <button
                        type="button"
                        onClick={() => {
                            setEmail('demo@archi247.com');
                            setPassword('123456');
                        }}
                        className="w-full bg-[#1e293b]/45 hover:bg-[#1e293b]/80 border border-slate-700 text-slate-200 font-bold py-3 rounded-2xl transition-all flex items-center justify-center gap-2 cursor-pointer text-xs"
                    >
                        <ShieldCheck size={16} className="text-emerald-400" /> Probar Demo de Cliente (Acceso Rápido)
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Login;
